import React from 'react';
import {useTranslation} from 'react-i18next';
import GalleryItem from './GalleryItem';
import {ArtisticObjectListing} from '../../admin/artisticobjects/types/ArtisticObject';
import {getThumbnailUrl} from '../../common/utils/imageUtils';

interface GalleryListingProps {
  artisticObjects: ArtisticObjectListing[];
  pagination: React.ReactNode;
  zoomedIn: boolean;
  withDescriptions: boolean;
  columnsCount: number;
}

interface GridColumns {
  xl: number;
  lg: number;
  md: number;
  default: number;
}

interface GridConfig {
  [columnsCount: number]: GridColumns;
}

const GRID_SIZES: GridConfig = {
  8: {xl: 8, lg: 6, md: 4, default: 2},
  6: {xl: 6, lg: 4, md: 2, default: 1},
  4: {xl: 4, lg: 4, md: 2, default: 1},
  2: {xl: 2, lg: 2, md: 1, default: 1},
}

const GalleryListing: React.FC<GalleryListingProps> = ({
                                                         artisticObjects,
                                                         pagination,
                                                         zoomedIn,
                                                         withDescriptions,
                                                         columnsCount
                                                       }) => {
  const {t} = useTranslation();
  if (artisticObjects.length === 0) {
    return (
      <section id="gallery" className="bg-gray-100 py-8">
        <div className="container mx-auto px-4">
          <div className="text-center py-20">
            <div className="text-lg text-gray-600">{t('gallery.listing.noObjectsFound')}</div>
          </div>
        </div>
      </section>
    );
  }

  const gridSize = GRID_SIZES[columnsCount] || GRID_SIZES[4];

  return (
    <section id="gallery" className="bg-gray-100 py-8">
      <div className="container mx-auto px-4">
        <div className="hidden grid
          grid-cols-1 grid-cols-2 grid-cols-4 grid-cols-6 grid-cols-8
          md:grid-cols-1 md:grid-cols-2 md:grid-cols-4 md:grid-cols-6 md:grid-cols-8
          lg:grid-cols-1 lg:grid-cols-2 lg:grid-cols-4 lg:grid-cols-6 lg:grid-cols-8
          xl:grid-cols-1 xl:grid-cols-2 xl:grid-cols-4 xl:grid-cols-6 xl:grid-cols-8"/>
        {/*TODO this is a hack to generate all tailwind classes */}
        <div className={`gap-6 grid 
            grid-cols-${gridSize.default} 
            md:grid-cols-${gridSize.md} 
            lg:grid-cols-${gridSize.lg} 
            xl:grid-cols-${gridSize.xl}`
        }>

          {artisticObjects.map((artisticObject) => {
            const firstImage = artisticObject.firstImage;
            const imageSrc = firstImage ? getThumbnailUrl(firstImage) : '/images/placeholder.jpg';
            const imageAlt = firstImage?.originalName || artisticObject.name;
            const caption = withDescriptions
              ? (artisticObject.childrenCount ? t('gallery.item.multipleObjects') : undefined)
              : artisticObject.name;

            return (
              <GalleryItem
                key={artisticObject.id}
                title={artisticObject.name}
                location={artisticObject.place.name}
                image={{
                  src: imageSrc,
                  alt: imageAlt,
                  focalPoint: firstImage?.focalPoint,
                  size: firstImage ? {width: firstImage.width, height: firstImage.height} : undefined
                }}
                caption={caption}
                dating={artisticObject.creationTime}
                zoomedIn={zoomedIn}
                withDescriptions={withDescriptions}
                artisticObjectId={artisticObject.id}
                placeId={artisticObject.place.id}
              />
            );
          })}
        </div>

        {pagination}
      </div>
    </section>
  );
};

export default GalleryListing;
